import moment from 'moment'

import {
  Person as PersonIcon,
  CalendarArrowRight as CalendarArrowRightIcon,
  CheckCircleLine as CheckCircleLineIcon,
  List as ListIcon,
} from '@factorialco/f0-react/icons/app'
import { type FormsCustomTemplatesQuery } from 'generated/resources/graphql'

import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import { F0OneIconProps } from '@factorialco/f0-react'
import { ForwardRefExoticComponent } from 'react'

export type FormCustomTemplate =
  FormsCustomTemplatesQuery['forms']['formsConnection']['nodes'][number]

const MAX_TEXT_CHARACTERS = 30

export type ColumnRenderer = (item: FormCustomTemplate) => {
  type: 'text' | 'dotTag' | 'status' | 'person'
  value: {
    text?: React.ReactNode
    label?: string
    color?: string
    status?: string
    src?: string
    firstName?: string
    lastName?: string
  }
}

export enum TableColumnType {
  Name = 'name',
  FormType = 'formType',
  Status = 'status',
  Author = 'author',
  CreatedAt = 'createdAt',
  QuestionsCount = 'questionsCount',
}

export const DEFAULT_TABLE_COLUMNS: TableColumnType[] = [
  TableColumnType.Name,
  TableColumnType.Status,
  TableColumnType.Author,
  TableColumnType.QuestionsCount,
  TableColumnType.CreatedAt,
]

export const DEFAULT_CARD_COLUMNS: TableColumnType[] = [
  TableColumnType.Name,
  TableColumnType.Status,
  TableColumnType.Author,
  TableColumnType.CreatedAt,
]

export type TagColumnConfig = {
  valueMapping?: Record<string, string>
  colorMapping?: Record<string, string>
}

export type StatusColumnConfig = {
  valueMapping?: Record<string, string>
  colorMapping?: Record<string, string>
}

export type ColumnConfigurations = {
  [TableColumnType.FormType]?: TagColumnConfig
  [TableColumnType.Status]?: StatusColumnConfig
}

export const getDefaultColumnConfigurations = (): ColumnConfigurations => ({
  [TableColumnType.Status]: {
    valueMapping: {
      draft: i18n.t('forms.custom_templates.data_collection.status_values.draft'),
      active: i18n.t('forms.custom_templates.data_collection.status_values.published'),
    },
    colorMapping: {
      draft: 'warning',
      active: 'positive',
    },
  },
})

export const createNameCell: ColumnRenderer = (item: FormCustomTemplate) => {
  const title = item?.title ?? ''

  return {
    type: 'text' as const,
    value: {
      text: title.length > MAX_TEXT_CHARACTERS ? title.slice(0, MAX_TEXT_CHARACTERS) + '…' : title,
    },
  }
}

const createFormTypeCell =
  (config?: TagColumnConfig): ColumnRenderer =>
  (item: FormCustomTemplate) => {
    const type = item?.formType
    const displayText = config?.valueMapping?.[type] ?? ''
    const color = config?.colorMapping?.[type] ?? 'neutral'
    return {
      type: 'dotTag' as const,
      value: {
        label: displayText,
        color: color,
      },
    }
  }

const createStatusCell =
  (config?: StatusColumnConfig): ColumnRenderer =>
  (item: FormCustomTemplate) => {
    const status = item?.status
    const displayText = config?.valueMapping?.[status] ?? ''
    const color = config?.colorMapping?.[status] ?? ''
    return {
      type: 'status' as const,
      value: {
        label: displayText,
        status: color,
      },
    }
  }

const createAuthorCell: ColumnRenderer = (item: FormCustomTemplate) => {
  return {
    type: 'person' as const,
    value: {
      src: buildUrl(item.author?.employee?.avatar?.url ?? ''),
      firstName: item.author?.employee?.firstName ?? '',
      lastName: item.author?.employee?.lastName ?? '',
    },
  }
}

const createCreatedAtCell: ColumnRenderer = (item: FormCustomTemplate) => {
  const createdAt = item.createdAt ? moment(item.createdAt).fromNow() : ''

  return {
    type: 'text' as const,
    value: {
      text: createdAt,
    },
  }
}

const createQuestionsCountCell: ColumnRenderer = (item: FormCustomTemplate) => ({
  type: 'text' as const,
  value: {
    text: item.questionsCount?.toString() ?? '-',
  },
})

export type ColumnDefinition = {
  label: () => string
  icon?: ForwardRefExoticComponent<F0OneIconProps>
  render: (configurations?: ColumnConfigurations) => ColumnRenderer
}

export const COLUMN_DEFINITIONS: Record<TableColumnType, ColumnDefinition> = {
  [TableColumnType.Name]: {
    label: () => i18n.t('forms.custom_templates.data_collection.name'),
    render: () => createNameCell,
  },
  [TableColumnType.FormType]: {
    label: () => i18n.t('forms.custom_templates.data_collection.type'),
    render: (configurations) => createFormTypeCell(configurations?.[TableColumnType.FormType]),
  },
  [TableColumnType.Status]: {
    label: () => i18n.t('forms.custom_templates.data_collection.status'),
    icon: CheckCircleLineIcon,
    render: (configurations) => createStatusCell(configurations?.[TableColumnType.Status]),
  },
  [TableColumnType.Author]: {
    label: () => i18n.t('forms.custom_templates.data_collection.author'),
    icon: PersonIcon,
    render: () => createAuthorCell,
  },
  [TableColumnType.QuestionsCount]: {
    label: () => i18n.t('forms.custom_templates.data_collection.questions_count'),
    icon: ListIcon,
    render: () => createQuestionsCountCell,
  },
  [TableColumnType.CreatedAt]: {
    label: () => i18n.t('common.date.date'),
    icon: CalendarArrowRightIcon,
    render: () => createCreatedAtCell,
  },
}
